import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { PathType, isPathType } from 'src/app/core/enums/path-type.enum';
import * as fromRoot from '../../../core/state/core.state';
import * as fromPath from '../../../core/state/path';
import * as fromContext from '../../../core/state/context';
import * as fromDevis from 'src/app/core/state/devis';
import * as fromOfferCustomization from 'src/app/core/state/offer-customization';
import * as fromOffer from 'src/app/core/state/offer-customization';
import * as fromTarification from 'src/app/core/state/tarification';
import * as fromContract from 'src/app/core/state/contract';
import * as fromPayment from 'src/app/core/state/payment';
import * as fromSubscriber from 'src/app/core/state/subscriber';
import * as fromAddress from 'src/app/core/state/address';
import * as fromHousing from 'src/app/core/state/housing';
import { BackendContext } from 'src/app/core/models/context.model';
import {
  MontantValeurObjets2000,
  Formule,
  SaisieFormule,
  TypePromo,
  QualiteOccupant,
  Civilite
} from 'src/app/core/models/form-state.model';
import { AppConfig } from 'src/app/app.config';

@Component({
  selector: 'app-redirect',
  templateUrl: './redirect.component.html'
})
export class RedirectComponent implements OnInit {
  pathType: PathType | null = null;
  montantValeurObjets2000: MontantValeurObjets2000 | null = null;
  qualiteOccupant: QualiteOccupant | undefined;
  contextId: string | null = null;
  contextData: BackendContext | null = null;

  constructor(
    @Inject(AppConfig) private appConfig: AppConfig,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<fromRoot.State>
  ) {
    if (this.router.url.includes('/start')) {
      const contextValue = this.route.snapshot.paramMap.get('context');
      if (contextValue !== null && contextValue !== '') {
        this.contextId = contextValue;

        // check for nested route for new backend context
        const productValue = this.route.snapshot.paramMap.get('product')?.toUpperCase() || '';
        if (productValue == null || productValue === '') {
          const contextObj = JSON.parse(localStorage.getItem('context') || 'null');
          if (contextObj != null) {
            this.contextData = contextObj.Contexte;
            return; // OK
          } else {
            console.warn('Failed to retrieve context data from localStorage');
            // fallthrough
          }
        } else {
          // new context for selected product
          if (isPathType(productValue)) {
            this.pathType = productValue;
            return; // OK
          } else {
            console.warn('Invalid product code', productValue);
            // fallthrough
          }
        }
      }
    }
  }

  ngOnInit() {
    this.store.dispatch(fromContext.setContextId({ contextId: this.contextId! }));

    if (this.contextData != null) {
      // update state using context data
      this.store.dispatch(fromContext.setContextData({ payload: this.contextData }));

      switch (this.contextData.Produit) {
        case 'HM_AHC':
        case 'VYV_AHC':
          this.pathType = PathType.HOUSING;
          break;
        case 'VYV_AHP':
          this.pathType = PathType.HOUSING_PARIS;
          break;
        case 'VYV_AHS':
          this.pathType = PathType.HOUSING_SOLIDARITY;
          break;
        default:
          this.pathType = PathType.UNDEFINED;
          break;
      }
    }

    console.log('Using context ID', this.contextId, 'and product', this.pathType);

    // determine where to redirect from context data
    if (this.contextData?.RefContrat != null) {
      if (this.contextData.Contrat != null) {
        this.store.dispatch(
          fromContract.patchContract({
            payload: {
              EstSansEffet: this.contextData.Contrat.EstSansEffet,
              NumeroContrat: this.contextData.RefContrat,
              RefDocumentCP: this.contextData.Contrat.RefDocumentCP,
              RefDocumentFIC: this.contextData.Contrat.RefDocumentFIC,
              RefDocumentMandatSEPA: this.contextData.Contrat.RefDocumentMandatSEPA,
              RefDocumentCG: this.contextData.Contrat.RefDocumentCG,
              RefDocumentAttestation: this.contextData.Contrat.RefDocumentAttestation,
              RefDocumentsSI: this.contextData.Contrat.RefDocumentsSI
            }
          })
        );
        this.store.dispatch(
          fromPayment.patchPayment({
            payload: {
              Telephone: this.contextData.Contrat.NumeroTelephoneSignature ?? undefined
            }
          })
        );
      }

      this.store.dispatch(fromPath.updateType({ pathType: this.pathType || PathType.UNDEFINED }));

      // cas de reprise signature
      if (this.contextData.Contrat?.EstSigne) {
        this.router.navigate(['/confirmcontract']);
      } else {
        this.router.navigate(['/electronicsignature']);
      }

      return;
    } else if (this.contextData?.RefDevis != null) {
      if (this.contextData.Devis != null) {
        this.store.dispatch(
          fromDevis.patchDevis({
            payload: {
              NumeroDevis: this.contextData.Devis.NumeroDevis,
              GDAPositif: this.contextData.Devis.GDAPositif,
              PPEPositif: this.contextData.Devis.PPEPositif,
              PrimeMensuelle: this.contextData.Devis.PrimeMensuelle,
              PrimeAnnuelle: this.contextData.Devis.PrimeAnnuelle,
              PrimesOptions: this.contextData.Devis.PrimesOptions,
              RefDocumentCG: this.contextData.Devis.RefDocumentCG,
              RefDocumentDevis: this.contextData.Devis.RefDocumentDevis,
              RefDocumentFIC: this.contextData.Devis.RefDocumentFIC,
              RefDocumentInfo: this.contextData.Devis.RefDocumentInfo
            }
          })
        );

        this.store.dispatch(
          fromOfferCustomization.patchSaisieFormule({
            payload: {
              FormuleSelectionnee: this.contextData.Devis.CodeFormule as Formule,
              Valeur: this.contextData.Devis.FormuleValeur
            }
          })
        );

        this.store.dispatch(
          fromSubscriber.patchSubscriber({
            payload: { Telephone: this.contextData.Devis.SouscripteurNumeroTel }
          })
        );

        this.store.dispatch(
          fromAddress.patchEsh({
            payload: { NomBailleur: this.contextData.Devis.NomBailleur }
          })
        );

        switch (this.contextData.Devis.LibelleQualiteOccupant) {
          case 'Propriétaire':
            this.qualiteOccupant = QualiteOccupant.Proprietaire;
            break;
          case 'Locataire':
            this.qualiteOccupant = QualiteOccupant.Locataire;
            break;
        }

        this.store.dispatch(
          fromHousing.patchOccupantType({
            payload: { QualiteOccupant: this.qualiteOccupant }
          })
        );

        switch (
          this.contextData.Devis.TarifSaisiePersonnalisation?.MontantValeurObjets2000?.toString()
        ) {
          case 'Montant5000':
            this.montantValeurObjets2000 = MontantValeurObjets2000.Montant5000;
            break;
          case 'Montant10000':
            this.montantValeurObjets2000 = MontantValeurObjets2000.Montant10000;
            break;
          case 'Montant15000':
            this.montantValeurObjets2000 = MontantValeurObjets2000.Montant20000;
            break;
          default:
            this.montantValeurObjets2000 = MontantValeurObjets2000.Montant5000;
            break;
        }

        this.store.dispatch(
          fromOfferCustomization.patchOfferCustomization({
            payload: {
              ProtectionVelos: this.contextData.Devis.TarifSaisiePersonnalisation?.ProtectionVelos,
              ProtectionJuridique:
                this.contextData.Devis.TarifSaisiePersonnalisation?.ProtectionJuridique,
              ProtectionNomades:
                this.contextData.Devis.TarifSaisiePersonnalisation?.ProtectionNomades,
              AssuranceScolaire:
                this.contextData.Devis.TarifSaisiePersonnalisation?.AssuranceScolaire,
              MontantValeurObjets2000: this.montantValeurObjets2000
            }
          })
        );

        const promo = this.contextData.Devis.Promotion;
        if (promo != null) {
          this.store.dispatch(
            fromOffer.patchSaisieFormule({
              payload: {
                CodePromo: 'REPRISE'
              } as Partial<SaisieFormule>
            })
          );

          this.store.dispatch(
            fromTarification.patchTarificationPromo({
              payload: {
                CampagneEnCours: promo.CampagneEnCours,
                CodePromoApplique: promo.CodePromoApplique,
                CodePromoValide: promo.CodePromoValide,
                DetailsPromo: promo.DetailsPromo,
                TypePromo: (promo.TypePromo as TypePromo) || undefined
              }
            })
          );
        }
      }
      if (this.pathType) {
        this.store.dispatch(fromPath.updateType({ pathType: this.pathType }));
      }
      // cas de reprise à partir du devis
      this.router.navigate(['/confirmquote']);
      return;
    } else if (this.pathType) {
      this.store.dispatch(fromPath.updateType({ pathType: this.pathType }));

      switch (this.pathType) {
        case PathType.HOUSING:
        case PathType.HOUSING_PARIS:
          if (this.appConfig.getConfig('brand') === 'HM') {
            this.store.dispatch(
              fromSubscriber.patchSubscriber({
                payload: {
                  Civilite:
                    this.contextData?.Souscripteur?.civilite === 1
                      ? Civilite.Monsieur
                      : this.contextData?.Souscripteur?.civilite === 2
                      ? Civilite.Madame
                      : undefined,
                  Nom: this.contextData?.Souscripteur?.nom,
                  Prenom: this.contextData?.Souscripteur?.prenom,
                  Email: this.contextData?.Souscripteur?.email,
                  DateNaissance: this.contextData?.Souscripteur?.dateNaissance,
                  LieuNaissance: this.contextData?.Souscripteur?.villeNaissance,
                  PaysNaissance: this.contextData?.Souscripteur?.paysNaissance,
                  Telephone: this.contextData?.Souscripteur?.telephone,
                  EstClient: !!this.contextData?.Souscripteur
                }
              })
            );
          }
          this.router.navigate(['/address']);
          return;
        case PathType.HOUSING_SOLIDARITY:
          this.router.navigate(['/solidarityaddress']);
          return;
        default:
        // fallthrough
      }
    }

    // default 404
    this.router.navigate(['/404']);
  }
}
